// 학생 대시보드 페이지

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CardActionArea,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useStudentAuth } from '../hooks/useStudentAuth';

interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  color: string;
}

const quickActions: QuickAction[] = [
  {
    id: 'attendance',
    title: '출결 현황',
    description: '이번 주 출석 기록을 확인하세요',
    icon: <CheckCircleIcon sx={{ fontSize: 40 }} />,
    path: '/student/attendance',
    color: '#4caf50',
  },
  {
    id: 'study-time',
    title: '순공부시간',
    description: '공부 시간과 순위를 확인하세요',
    icon: <AccessTimeIcon sx={{ fontSize: 40 }} />,
    path: '/student/study-time',
    color: '#2196f3',
  },
  {
    id: 'penalty',
    title: '벌점 현황',
    description: '벌점 내역을 확인하세요',
    icon: <WarningAmberIcon sx={{ fontSize: 40 }} />,
    path: '/student/penalty',
    color: '#ff9800',
  },
  {
    id: 'schedule',
    title: '교시 스케줄',
    description: '교시 스케줄을 확인하세요',
    icon: <CalendarMonthIcon sx={{ fontSize: 40 }} />,
    path: '/student/period-schedule',
    color: '#9c27b0',
  },
];

export default function StudentDashboard() {
  const navigate = useNavigate();
  const { student, isLoading } = useStudentAuth();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Typography>로딩 중...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* 환영 메시지 */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          안녕하세요, {student?.name || '학생'}님!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          오늘도 열심히 공부해요.
        </Typography>
      </Box>

      {/* 빠른 메뉴 */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        빠른 메뉴
      </Typography>

      <Grid container spacing={3}>
        {quickActions.map((action) => (
          <Grid size={{ xs: 12, sm: 6 }} key={action.id}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardActionArea onClick={() => navigate(action.path)} sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: `${action.color}15`,
                      color: action.color,
                    }}
                  >
                    {action.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      {action.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {action.description}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* 알림 카드 */}
      <Card sx={{ mt: 4, backgroundColor: '#e3f2fd' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} color="primary" gutterBottom>
            오늘의 목표
          </Typography>
          <Typography variant="body1" color="text.secondary">
            순공부시간 4시간 이상 달성하면 포인트가 적립됩니다!
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
